import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import styled, { keyframes } from 'styled-components';
import { toast } from 'react-toastify';
import { CheckCircle, AlertTriangle, Loader, Mail } from 'lucide-react';
import { useTheme } from '../context/ThemeContext';
import { ThemedText, Button, Input } from '../components';
import authService from '../api/services/authService';

// --- Animations ---

const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(24px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const gradientShift = keyframes`
  0% { background-position: 0% 50%; }
  50% { background-position: 100% 50%; }
  100% { background-position: 0% 50%; }
`;

const bubbleFloat = keyframes`
  0% {
    transform: translateY(100%) scale(0);
    opacity: 0;
  }
  10% { opacity: 1; }
  100% {
    transform: translateY(-100vh) scale(1);
    opacity: 0;
  }
`;

const pulseRing = keyframes`
  0% { transform: scale(1); opacity: 0.4; }
  50% { transform: scale(1.15); opacity: 0.8; }
  100% { transform: scale(1); opacity: 0.4; }
`;

const spin = keyframes`
  to { transform: rotate(360deg); }
`;

// --- Styled Components ---

const PageContainer = styled.div`
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #0f1229 0%, #232562 30%, #2d3070 55%, #3a6b8a 75%, #59caee 100%);
  background-size: 300% 300%;
  animation: ${gradientShift} 12s ease infinite;
  padding: ${p => p.theme.spacing.xl};
  position: relative;
  overflow: hidden;
`;

const Bubble = styled.div`
  position: absolute;
  bottom: -20px;
  border-radius: 50%;
  background: radial-gradient(circle at 30% 30%, rgba(89, 202, 238, 0.2), rgba(89, 202, 238, 0.03));
  border: 1px solid rgba(89, 202, 238, 0.1);
  animation: ${bubbleFloat} ${p => p.$duration}s linear infinite;
  animation-delay: ${p => p.$delay}s;
  left: ${p => p.$left}%;
  width: ${p => p.$size}px;
  height: ${p => p.$size}px;
  pointer-events: none;
`;

const FloatingBubbles = () => {
  const bubbles = [
    { $size: 10, $left: 12, $duration: 15, $delay: 0 },
    { $size: 16, $left: 35, $duration: 12, $delay: 3 },
    { $size: 7, $left: 58, $duration: 18, $delay: 1 },
    { $size: 20, $left: 78, $duration: 14, $delay: 5 },
    { $size: 9, $left: 92, $duration: 21, $delay: 2 },
    { $size: 13, $left: 22, $duration: 16, $delay: 7 },
  ];
  return <>{bubbles.map((b, i) => <Bubble key={i} {...b} />)}</>;
};

const Card = styled.div`
  width: 100%;
  max-width: 440px;
  background: rgba(255, 255, 255, 0.07);
  backdrop-filter: blur(24px);
  -webkit-backdrop-filter: blur(24px);
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 20px;
  padding: 40px;
  position: relative;
  z-index: 10;
  animation: ${fadeInUp} 0.8s ease-out;
  text-align: center;

  @media (max-width: 480px) {
    padding: 28px 24px;
  }
`;

const IconCircle = styled.div`
  width: 64px;
  height: 64px;
  border-radius: 50%;
  background: ${p => p.$variant === 'success'
    ? 'rgba(40, 167, 69, 0.12)'
    : p.$variant === 'error'
      ? 'rgba(220, 53, 69, 0.12)'
      : 'rgba(89, 202, 238, 0.12)'};
  border: 1px solid ${p => p.$variant === 'success'
    ? 'rgba(40, 167, 69, 0.25)'
    : p.$variant === 'error'
      ? 'rgba(220, 53, 69, 0.25)'
      : 'rgba(89, 202, 238, 0.25)'};
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto ${p => p.theme.spacing.lg};
  position: relative;

  &::after {
    content: '';
    position: absolute;
    inset: -6px;
    border-radius: 50%;
    border: 1px solid ${p => p.$variant === 'success'
      ? 'rgba(40, 167, 69, 0.15)'
      : p.$variant === 'error'
        ? 'rgba(220, 53, 69, 0.15)'
        : 'rgba(89, 202, 238, 0.15)'};
    animation: ${pulseRing} 3s ease-in-out infinite;
  }
`;

const LogoImage = styled.img`
  width: 110px;
  height: auto;
  margin-bottom: ${p => p.theme.spacing.lg};
  filter: brightness(0) invert(1);
  opacity: 0.9;
`;

const Spinner = styled(Loader)`
  animation: ${spin} 1s linear infinite;
`;

const ErrorMessage = styled.div`
  background: rgba(220, 53, 69, 0.1);
  border: 1px solid rgba(220, 53, 69, 0.25);
  border-radius: ${p => p.theme.borderRadius.md};
  padding: ${p => p.theme.spacing.md};
  color: #ff7b8a;
  text-align: center;
  font-size: ${p => p.theme.typography.sizes?.small};
  margin-bottom: ${p => p.theme.spacing.lg};
`;

const BackLink = styled.button`
  background: none;
  border: none;
  color: rgba(89, 202, 238, 0.9);
  cursor: pointer;
  font-size: ${p => p.theme.typography.sizes?.small};
  padding: 0;
  margin-top: ${p => p.theme.spacing.md};
  font-weight: 500;
  transition: opacity 0.2s;

  &:hover {
    opacity: 0.7;
  }
`;

const ResendForm = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${p => p.theme.spacing.md};
  margin-top: ${p => p.theme.spacing.lg};
`;

// --- Component ---

export const VerifyEmailPage = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const [status, setStatus] = useState('loading'); // loading, success, error
  const [error, setError] = useState('');
  const [resendEmail, setResendEmail] = useState('');
  const [resendLoading, setResendLoading] = useState(false);
  const [showResend, setShowResend] = useState(false);
  const theme = useTheme();
  const navigate = useNavigate();
  const verifiedRef = useRef(false);

  useEffect(() => {
    if (!token) {
      setStatus('error');
      setError('Invalid verification link. No token provided.');
      return;
    }

    if (verifiedRef.current) return;
    verifiedRef.current = true;

    const verify = async () => {
      try {
        await authService.verifyEmail(token);
        setStatus('success');
      } catch (err) {
        setStatus('error');
        const message = err?.response?.data?.message || err?.message || 'Verification failed. The link may have expired.';
        setError(message);
      }
    };

    verify();
  }, [token]);

  const handleResend = async () => {
    if (!resendEmail) return;
    setResendLoading(true);
    try {
      await authService.resendVerification(resendEmail);
      toast.success('If an account with that email exists, a new verification email has been sent.');
      setShowResend(false);
    } catch {
      toast.error('Failed to resend verification email. Please try again.');
    } finally {
      setResendLoading(false);
    }
  };

  // Loading state
  if (status === 'loading') {
    return (
      <PageContainer theme={theme}>
        <FloatingBubbles />
        <Card theme={theme}>
          <LogoImage src="/img/aqrate-logo.png" alt="aQRate Logo" theme={theme} />
          <IconCircle theme={theme}>
            <Spinner size={28} color="white" />
          </IconCircle>
          <ThemedText type="title" style={{ fontSize: '24px', marginBottom: theme.spacing.md, color: '#fff', fontWeight: 700 }}>
            Verifying Email
          </ThemedText>
          <ThemedText type="default" style={{ color: 'rgba(255,255,255,0.7)', fontSize: '14px', lineHeight: '1.6' }}>
            Please wait while we verify your email address...
          </ThemedText>
        </Card>
      </PageContainer>
    );
  }

  // Success state
  if (status === 'success') {
    return (
      <PageContainer theme={theme}>
        <FloatingBubbles />
        <Card theme={theme}>
          <LogoImage src="/img/aqrate-logo.png" alt="aQRate Logo" theme={theme} />
          <IconCircle theme={theme} $variant="success">
            <CheckCircle size={28} color="#28a745" />
          </IconCircle>
          <ThemedText type="title" style={{ fontSize: '24px', marginBottom: theme.spacing.md, color: '#fff', fontWeight: 700 }}>
            Email Verified
          </ThemedText>
          <ThemedText type="default" style={{ color: 'rgba(255,255,255,0.7)', marginBottom: theme.spacing.xl, fontSize: '14px', lineHeight: '1.6' }}>
            Your email has been verified successfully. You can now sign in to your account.
          </ThemedText>
          <Button size="lg" onClick={() => navigate('/login')}>
            Sign In
          </Button>
        </Card>
      </PageContainer>
    );
  }

  // Error state
  return (
    <PageContainer theme={theme}>
      <FloatingBubbles />
      <Card theme={theme}>
        <LogoImage src="/img/aqrate-logo.png" alt="aQRate Logo" theme={theme} />
        <IconCircle theme={theme} $variant="error">
          <AlertTriangle size={28} color="#dc3545" />
        </IconCircle>
        <ThemedText type="title" style={{ fontSize: '24px', marginBottom: theme.spacing.md, color: '#fff', fontWeight: 700 }}>
          Verification Failed
        </ThemedText>
        <ErrorMessage theme={theme}>{error}</ErrorMessage>

        {!showResend ? (
          <>
            <BackLink theme={theme} type="button" onClick={() => setShowResend(true)}>
              <Mail size={14} style={{ marginRight: 6, verticalAlign: 'middle' }} />
              Resend Verification Email
            </BackLink>
            <br />
            <BackLink theme={theme} type="button" onClick={() => navigate('/login')}>
              Back to Sign In
            </BackLink>
          </>
        ) : (
          <ResendForm theme={theme}>
            <Input
              label="Email Address"
              type="email"
              value={resendEmail}
              onChange={(e) => setResendEmail(e.target.value)}
              placeholder="Enter your email"
            />
            <Button size="lg" onClick={handleResend} disabled={resendLoading || !resendEmail}>
              {resendLoading ? 'Sending...' : 'Resend Verification'}
            </Button>
            <BackLink theme={theme} type="button" onClick={() => navigate('/login')}>
              Back to Sign In
            </BackLink>
          </ResendForm>
        )}
      </Card>
    </PageContainer>
  );
};
